import { motion } from "framer-motion";
import { Camera, ArrowUpRight, Phone } from "lucide-react";
import { CONTACTS } from "@/data/site";

const FLEET_PHOTOS = [
  {
    src: "/assets/tow-cascadia.jpg",
    caption: "Roadside Recovery / Volvo VNL",
  },
  {
    src: "/assets/tow-mixer.jpg",
    caption: "Unit 01 / Cement Mixer Tow",
  },
  {
    src: "/assets/tow-volvo.jpg",
    caption: "Wrecker 01 / Cascadia Hookup",
  },
];

export default function Gallery() {
  return (
    <section id="gallery" className="relative py-24 lg:py-32 bg-carbon overflow-hidden" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-hazard font-semibold mb-3">/ The Fleet & The Shop</p>
            <h2 className="font-heading uppercase font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
              Iron, Chrome <span className="chrome-text">& Horsepower</span>
            </h2>
          </div>
          <p className="max-w-md text-slate-400 text-sm leading-relaxed flex items-start gap-2">
            <Camera className="w-4 h-4 text-hazard shrink-0 mt-0.5" />
            Straight off the shop floor and the interstate — the machines we keep moving.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5 relative group"
            data-testid="gallery-flyer-frame"
          >
            <div className="absolute -inset-6 bg-[radial-gradient(ellipse_at_center,rgba(255,184,0,0.12),transparent_65%)] pointer-events-none" />
            <div className="relative border border-steel overflow-hidden h-full min-h-[420px] bg-surface">
              <img
                src="/assets/gsx-flyer.png"
                alt="GSX Diesel Truck Repair official flyer — heavy duty towing and repair services"
                className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-carbon/85 via-transparent to-carbon/30 pointer-events-none" />
              <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest text-hazard bg-carbon/80 backdrop-blur px-3 py-1.5 border border-steel">
                Official Shop Flyer
              </span>
              <div className="absolute bottom-0 inset-x-0 p-5">
                <p className="font-heading font-black uppercase text-2xl text-white leading-tight">The GSX Promise</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mt-1">
                  Diagnostics • Repair • 24/7 Heavy Towing — Bloomington CA
                </p>
              </div>
            </div>
            <div className="absolute -top-3 -left-3 w-20 h-20 hazard-stripes opacity-60 -z-10" />
          </motion.div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {FLEET_PHOTOS.map((p, i) => (
              <motion.figure
                key={p.caption}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className={`group relative border border-steel overflow-hidden bg-surface hover:border-hazard/60 transition-colors duration-300 ${i % 2 === 1 ? "sm:translate-y-6" : ""}`}
                data-testid={`gallery-image-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={p.src}
                    alt={p.caption}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale-[0.35] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
                  />
                </div>
                <figcaption className="absolute bottom-0 inset-x-0 flex items-center justify-between gap-2 px-4 py-3 bg-carbon/85 backdrop-blur border-t border-steel">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-300">{p.caption}</span>
                  <ArrowUpRight className="w-4 h-4 text-hazard opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </figcaption>
              </motion.figure>
            ))}
            <motion.a
              href={CONTACTS.ray.tel}
              data-testid="gallery-cta-tile"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="group relative border border-hazard/50 bg-surface overflow-hidden flex flex-col items-center justify-center gap-3 p-8 sm:translate-y-6 hover:bg-hazard transition-colors duration-300"
            >
              <span className="absolute top-0 left-0 right-0 h-1 hazard-stripes opacity-70" />
              <Phone className="w-7 h-7 text-hazard group-hover:text-carbon transition-colors duration-300" />
              <span className="font-heading font-black uppercase text-2xl text-white group-hover:text-carbon text-center leading-tight transition-colors duration-300">
                Your Rig Next?
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-hazard group-hover:text-carbon/80 transition-colors duration-300">
                Call Ray — {CONTACTS.ray.phone}
              </span>
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
}
